import React, { Component, type ComponentType, type ReactNode, type ErrorInfo } from 'react';

import {
	fireErrorAnalytics,
	VIEW_WHITELIST,
} from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { useInteractionContext, type LabelStack } from '@atlaskit/react-ufo/interaction-context';
import {
	useAnalyticsEvents,
	type UIAnalyticsEvent,
	type WithAnalyticsEventsProps,
} from '@atlaskit/analytics-next';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import type { AnalyticsAttributes } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';

interface Props extends WithAnalyticsEventsProps {
	// We are overloading `attributes` with a callback. For backwards compatibility we don't want to change the prop name

	attributes?: AnalyticsAttributes | ((arg1: Error) => AnalyticsAttributes);
	children: ReactNode;
	// this prop is coming from Atlaskit, can't rename it
	createAnalyticsEvent: NonNullable<WithAnalyticsEventsProps['createAnalyticsEvent']>;
	id: string;
	packageName?: string;
	teamName?: string;
	sendToPrivacyUnsafeSplunk?: boolean;
	ufoLabelStack?: LabelStack;
}

type State = {
	analyticsEvent: UIAnalyticsEvent | null;
};

// exported for testing
export const getSupportedView = (labelStack?: LabelStack | null) => {
	const view = labelStack?.find((label) => VIEW_WHITELIST.has(label.name));
	return view?.name;
};

class CatchAndReportErrors extends Component<Props, State> {
	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		const {
			packageName,
			teamName,
			id,
			attributes,
			createAnalyticsEvent,
			sendToPrivacyUnsafeSplunk,
			ufoLabelStack,
		} = this.props;
		const analyticsEvent = createAnalyticsEvent({ action: 'failed' });

		fireErrorAnalytics({
			event: analyticsEvent,
			errorInfo,
			error,
			meta: {
				id,
				packageName,
				teamName,
			},
			attributes: typeof attributes === 'function' ? attributes(error) : attributes,
			sendToPrivacyUnsafeSplunk,
			view: fg('jira-report-unhandled-errors-by-view')
				? getSupportedView(ufoLabelStack)
				: undefined,
		});

		throw error;
	}

	render() {
		return this.props.children;
	}
}

type ReportErrorsType = ComponentType<
	Flow.Diff<
		Props,
		{
			createAnalyticsEvent: Props['createAnalyticsEvent'];
		}
	>
>;

const ReportErrorsWithAnalytics: ReportErrorsType = (props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	return <CatchAndReportErrors {...props} createAnalyticsEvent={createAnalyticsEvent} />;
};

// When cleaning up jira-report-unhandled-errors-by-view combine into 1 wrapper for CatchAndReportErrors rather than 2
const ReportErrorsWithUFOContext: ReportErrorsType = (props) => {
	const interactionContext = useInteractionContext();
	return <ReportErrorsWithAnalytics {...props} ufoLabelStack={interactionContext?.labelStack} />;
};

export default componentWithFG(
	'jira-report-unhandled-errors-by-view',
	ReportErrorsWithUFOContext,
	ReportErrorsWithAnalytics,
);
